// État de l’application et règles de synchronisation.
// Règles non négociables :
//  - « Synchronisé » n’est posé qu’après une lecture réussie du serveur (loadAll résolue).
//  - Une erreur garde sa cause et propose Réessayer ; aucune donnée d’exemple, jamais.
//  - La copie locale n’est écrite qu’après une lecture réussie, et toujours affichée comme copie.
//  - Une écriture est optimiste mais annulée si le serveur refuse, avec la cause affichée.
import { DEFAULT_BOOKING, validateBookingSettings, type Booking, type BookingSettings } from '../core/booking';
import { DEFAULT_PROFILE, ValidationError, label, validateContact, validateProfile, type Contact, type Profile } from '../core/contact';
import { contactSignature, parseImport, serializeExport } from '../core/importExport';
import type { Auth, AuthUser } from './auth';
import { NoCache, type Cache } from './cache';
import { describeError, RepoError, type LiveStatus, type Repo, type RepoEvent, type Snapshot } from './repo';
import { respondBooking as respondBookingApi, type RespondResult } from './rdv';

export type Phase = 'boot' | 'signed-out' | 'loading' | 'synced' | 'error' | 'offline';

export interface Sync {
  phase: Phase;
  /** État du canal temps réel. */
  live: LiveStatus;
  /** Dernière lecture réussie du serveur (ou date de la copie locale affichée). */
  readAt: number | null;
  /** Cause, quand phase vaut error ou offline. */
  message: string;
  /** Écritures en cours. */
  pending: number;
  lastWriteAt: number | null;
  /** Les données affichées viennent de la copie locale, pas du serveur. */
  fromCache: boolean;
}

export interface State {
  user: AuthUser | null;
  profile: Profile;
  /** Un profil existe sur le serveur (sinon : valeurs par défaut, pas encore enregistrées). */
  profileSaved: boolean;
  /** Tous les contacts, corbeille comprise (deletedAt non nul). */
  contacts: Contact[];
  /** Réglages de la prise de rendez-vous. */
  booking: BookingSettings;
  bookingSaved: boolean;
  /** Rendez-vous pris via la page publique (récents et à venir). */
  bookings: Booking[];
  sync: Sync;
  /** Mode développement en mémoire : rien n’est enregistré. Jamais vrai en production. */
  dev: boolean;
}

export interface Notice {
  id: number;
  text: string;
  kind: 'info' | 'error';
  undo?: () => Promise<void>;
}

export const TRASH_DAYS = 30;

export interface StoreOptions {
  dev?: boolean;
  cache?: Cache;
  now?: () => number;
  isOnline?: () => boolean;
}

const initialSync: Sync = { phase: 'boot', live: 'connecting', readAt: null, message: '', pending: 0, lastWriteAt: null, fromCache: false };

function upsertList(list: Contact[], c: Contact): Contact[] {
  const i = list.findIndex((x) => x.id === c.id);
  if (i < 0) return [...list, c];
  const next = list.slice();
  next[i] = c;
  return next;
}

export class Store {
  private st: State;
  private listeners = new Set<() => void>();
  private noticeListeners = new Set<(n: Notice) => void>();
  private stopRepo: (() => void) | null = null;
  private stopAuth: (() => void) | null = null;
  private seq = 0;
  private readonly cache: Cache;
  private readonly now: () => number;
  private readonly isOnline: () => boolean;

  constructor(private readonly repo: Repo, private readonly auth: Auth, opts: StoreOptions = {}) {
    this.cache = opts.cache ?? new NoCache();
    this.now = opts.now ?? (() => Date.now());
    // Hors navigateur (tests), `navigator.onLine` n’existe pas : on considère la connexion présente.
    this.isOnline = opts.isOnline ?? (() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
    this.st = { user: null, profile: DEFAULT_PROFILE, profileSaved: false, contacts: [], booking: DEFAULT_BOOKING, bookingSaved: false, bookings: [], sync: initialSync, dev: !!opts.dev };
  }

  get state(): State { return this.st; }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  onNotice(fn: (n: Notice) => void): () => void {
    this.noticeListeners.add(fn);
    return () => this.noticeListeners.delete(fn);
  }

  private set(patch: Partial<State>): void {
    this.st = { ...this.st, ...patch };
    this.listeners.forEach((l) => l());
  }

  private setSync(patch: Partial<Sync>): void {
    this.set({ sync: { ...this.st.sync, ...patch } });
  }

  private notify(text: string, kind: Notice['kind'] = 'info', undo?: () => Promise<void>): void {
    const n: Notice = { id: ++this.seq, text, kind, ...(undo ? { undo } : {}) };
    this.noticeListeners.forEach((l) => l(n));
  }

  /** Contacts visibles (hors corbeille). */
  get active(): Contact[] { return this.st.contacts.filter((c) => !c.deletedAt); }
  /** Corbeille. */
  get trash(): Contact[] { return this.st.contacts.filter((c) => !!c.deletedAt); }
  /** Rendez-vous confirmés et demandes en attente à venir, du plus proche au plus lointain. */
  get upcomingBookings(): Booking[] {
    const now = this.now();
    return this.st.bookings.filter((b) => (b.status === 'confirmed' || b.status === 'pending') && b.endAt > now).sort((a, b) => a.startAt - b.startAt);
  }

  /** Demandes en attente de ma décision, les plus anciennes d’abord. */
  get pendingBookings(): Booking[] {
    return this.st.bookings.filter((b) => b.status === 'pending').sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
  }

  /** L’état vide (« aucun contact ») ne peut s’afficher qu’après une lecture réussie du serveur. */
  get canShowEmpty(): boolean { return this.st.sync.phase === 'synced' && this.active.length === 0; }

  // ── Démarrage et session ──────────────────────────────────────────────────

  async start(): Promise<void> {
    this.stopAuth?.();
    this.stopAuth = this.auth.onChange((u) => {
      const currentId = this.st.user?.id ?? null;
      if (u && u.id !== currentId) { this.set({ user: u }); void this.load(); }
      else if (!u && currentId) this.signedOut(currentId);
    });
    let u: AuthUser | null = null;
    try { u = await this.auth.current(); }
    catch (e) { this.setSync({ phase: 'error', message: describeError(e).message }); return; }
    if (!u) { this.set({ user: null, sync: { ...initialSync, phase: 'signed-out' } }); return; }
    if (this.st.user?.id === u.id && this.st.sync.phase !== 'boot') return; // déjà chargé par onChange
    this.set({ user: u });
    await this.load();
  }

  private signedOut(previousId: string): void {
    this.stopRepo?.();
    this.stopRepo = null;
    this.cache.clear(previousId);
    this.set({ user: null, contacts: [], profile: DEFAULT_PROFILE, profileSaved: false, booking: DEFAULT_BOOKING, bookingSaved: false, bookings: [], sync: { ...initialSync, phase: 'signed-out' } });
  }

  requestCode(email: string): Promise<void> { return this.auth.requestCode(email.trim()); }
  verifyCode(email: string, code: string): Promise<void> { return this.auth.verifyCode(email.trim(), code); }

  async signOut(): Promise<void> {
    const id = this.st.user?.id;
    await this.auth.signOut();
    if (id && this.st.user) this.signedOut(id);
  }

  // ── Lecture ───────────────────────────────────────────────────────────────

  /** Lecture complète du serveur. `quiet` : rafraîchissement sans passer par l’état « Chargement ». */
  async load(quiet = false): Promise<void> {
    const u = this.st.user;
    if (!u) return;
    if (!quiet) this.setSync({ phase: 'loading', message: '' });
    if (!this.isOnline()) { this.useCache(u.id, 'offline', 'Pas de connexion.'); return; }
    try {
      const snap = await this.repo.loadAll();
      this.apply(snap);
      this.cache.write(u.id, snap);
      this.setSync({ phase: 'synced', readAt: snap.readAt, message: '', fromCache: false });
      this.listen();
      void this.purgeOld();
    } catch (e) {
      const err = describeError(e);
      if (quiet && this.st.sync.phase === 'synced' && !err.offline) { this.notify(err.message, 'error'); return; }
      this.useCache(u.id, err.offline ? 'offline' : 'error', err.message);
    }
  }

  retry(): Promise<void> { return this.load(); }

  /** Appelé par l’interface quand le réseau change d’état. */
  setOnline(online: boolean): void {
    if (!this.st.user) return;
    if (online) { if (this.st.sync.phase === 'offline' || this.st.sync.phase === 'error') void this.load(); }
    else this.setSync({ phase: 'offline', message: 'Pas de connexion.' });
  }

  /** À la reprise de l’onglet : relit le serveur si le temps réel n’est pas actif. */
  refreshIfStale(): void {
    if (this.st.sync.phase === 'synced' && this.st.sync.live !== 'live') void this.load(true);
  }

  private apply(snap: Snapshot): void {
    this.set({
      contacts: snap.contacts, profile: snap.profile ?? DEFAULT_PROFILE, profileSaved: !!snap.profile,
      booking: snap.booking ?? DEFAULT_BOOKING, bookingSaved: !!snap.booking, bookings: snap.bookings ?? [],
    });
  }

  private useCache(userId: string, phase: 'offline' | 'error', message: string): void {
    const cached = this.cache.read(userId);
    if (cached) {
      this.apply(cached);
      this.setSync({ phase, message, readAt: cached.readAt, fromCache: true });
    } else {
      this.set({ contacts: [], profile: DEFAULT_PROFILE, profileSaved: false, booking: DEFAULT_BOOKING, bookingSaved: false, bookings: [] });
      this.setSync({ phase, message, fromCache: false });
    }
  }

  private cacheNow(): void {
    const u = this.st.user;
    if (!u || this.st.sync.fromCache) return;
    this.cache.write(u.id, { profile: this.st.profileSaved ? this.st.profile : null, contacts: this.st.contacts, readAt: this.st.sync.readAt ?? this.now(), booking: this.st.bookingSaved ? this.st.booking : null, bookings: this.st.bookings });
  }

  private listen(): void {
    this.stopRepo?.();
    this.stopRepo = this.repo.subscribe((e) => this.onRemote(e), (s) => this.setSync({ live: s }));
  }

  private onRemote(e: RepoEvent): void {
    if (e.type === 'contact') this.set({ contacts: upsertList(this.st.contacts, e.contact) });
    else if (e.type === 'contact-removed') this.set({ contacts: this.st.contacts.filter((c) => c.id !== e.id) });
    else if (e.type === 'booking') {
      const known = this.st.bookings.some((b) => b.id === e.booking.id);
      const list = known ? this.st.bookings.map((b) => (b.id === e.booking.id ? e.booking : b)) : [...this.st.bookings, e.booking];
      this.set({ bookings: list });
      if (!known && e.booking.status === 'confirmed') this.notify(`Nouveau rendez-vous : ${e.booking.name}.`, 'info');
      else if (!known && e.booking.status === 'pending') this.notify(`Demande de rendez-vous à valider : ${e.booking.name}.`, 'info');
    }
    else this.set({ profile: e.profile, profileSaved: true });
    this.setSync({ readAt: this.now() });
    this.cacheNow();
  }

  private async purgeOld(): Promise<void> {
    const limit = this.now() - TRASH_DAYS * 86_400_000;
    const old = this.trash.filter((c) => Date.parse(c.deletedAt!) < limit);
    for (const c of old) {
      try { await this.repo.purgeContact(c.id); this.set({ contacts: this.st.contacts.filter((x) => x.id !== c.id) }); }
      catch (e) { console.warn('Purge de la corbeille impossible', e); }
    }
  }

  // ── Écriture ──────────────────────────────────────────────────────────────

  private async write<T>(op: () => Promise<T>, optimistic: () => void, revert: () => void): Promise<T> {
    if (!this.st.user) throw new RepoError('Vous n’êtes pas connecté.');
    if (!this.isOnline() || this.st.sync.phase === 'offline') {
      throw new RepoError('Pas de connexion : la modification n’a pas été enregistrée.', true);
    }
    if (this.st.sync.phase !== 'synced') {
      throw new RepoError('Le serveur n’a pas encore répondu : réessayez dans un instant.');
    }
    optimistic();
    this.setSync({ pending: this.st.sync.pending + 1 });
    try {
      const r = await op();
      this.setSync({ pending: this.st.sync.pending - 1, lastWriteAt: this.now() });
      return r;
    } catch (e) {
      revert();
      const err = describeError(e);
      this.setSync({ pending: this.st.sync.pending - 1, ...(err.offline ? { phase: 'offline' as const, message: err.message } : {}) });
      this.notify(err.message, 'error');
      throw err;
    }
  }

  /** Crée ou modifie un contact. Les erreurs de saisie (ValidationError) remontent au formulaire. */
  async saveContact(input: object, existingId?: string): Promise<Contact> {
    const prev = existingId ? this.st.contacts.find((c) => c.id === existingId) : undefined;
    if (existingId && !prev) throw new ValidationError('Ce contact n’existe plus.');
    const merged: Record<string, unknown> = { ...(prev ?? {}), ...input };
    if (!prev) delete merged.id;
    const c = validateContact(merged, this.active.length, () => new Date(this.now()).toISOString());
    const before = this.st.contacts;
    const saved = await this.write(
      () => this.repo.saveContact(c),
      () => this.set({ contacts: upsertList(before, c) }),
      () => this.set({ contacts: before }),
    );
    this.set({ contacts: upsertList(this.st.contacts, saved) });
    this.cacheNow();
    return saved;
  }

  toggleFavorite(id: string): Promise<Contact> {
    const c = this.st.contacts.find((x) => x.id === id);
    if (!c) throw new ValidationError('Ce contact n’existe plus.');
    return this.saveContact({ favorite: !c.favorite }, id);
  }

  /** Suppression réversible : le contact passe dans la corbeille, « Annuler » le rétablit. */
  async deleteContact(id: string): Promise<void> {
    const c = this.st.contacts.find((x) => x.id === id);
    if (!c) return;
    const before = this.st.contacts;
    const deletedAt = new Date(this.now()).toISOString();
    const saved = await this.write(
      () => this.repo.deleteContact(id, deletedAt),
      () => this.set({ contacts: upsertList(before, { ...c, deletedAt }) }),
      () => this.set({ contacts: before }),
    );
    this.set({ contacts: upsertList(this.st.contacts, saved) });
    this.cacheNow();
    this.notify(`${label(c)} a été supprimé.`, 'info', () => this.restoreContact(id));
  }

  async restoreContact(id: string): Promise<void> {
    const c = this.st.contacts.find((x) => x.id === id);
    if (!c) return;
    const before = this.st.contacts;
    const saved = await this.write(
      () => this.repo.restoreContact(id),
      () => this.set({ contacts: upsertList(before, { ...c, deletedAt: null }) }),
      () => this.set({ contacts: before }),
    );
    this.set({ contacts: upsertList(this.st.contacts, saved) });
    this.cacheNow();
  }

  /** Suppression définitive (depuis la corbeille). */
  async purgeContact(id: string): Promise<void> {
    const before = this.st.contacts;
    await this.write(
      () => this.repo.purgeContact(id),
      () => this.set({ contacts: before.filter((c) => c.id !== id) }),
      () => this.set({ contacts: before }),
    );
    this.cacheNow();
  }

  async emptyTrash(): Promise<void> {
    for (const c of this.trash) await this.purgeContact(c.id);
  }

  async saveProfile(input: object): Promise<Profile> {
    const p = validateProfile({ ...this.st.profile, ...input });
    const before = { profile: this.st.profile, profileSaved: this.st.profileSaved };
    const saved = await this.write(
      () => this.repo.saveProfile(p),
      () => this.set({ profile: p, profileSaved: true }),
      () => this.set(before),
    );
    this.set({ profile: saved, profileSaved: true });
    this.cacheNow();
    return saved;
  }

  async saveBookingSettings(input: object): Promise<BookingSettings> {
    const s = validateBookingSettings({ ...this.st.booking, ...input });
    const before = { booking: this.st.booking, bookingSaved: this.st.bookingSaved };
    const saved = await this.write(
      () => this.repo.saveBookingSettings(s),
      () => this.set({ booking: s, bookingSaved: true }),
      () => this.set(before),
    );
    this.set({ booking: saved, bookingSaved: true });
    this.cacheNow();
    return saved;
  }

  async cancelBooking(id: string): Promise<void> {
    const b = this.st.bookings.find((x) => x.id === id);
    if (!b) return;
    const before = this.st.bookings;
    const saved = await this.write(
      () => this.repo.cancelBooking(id),
      () => this.set({ bookings: before.map((x) => (x.id === id ? { ...x, status: 'cancelled' as const } : x)) }),
      () => this.set({ bookings: before }),
    );
    this.set({ bookings: this.st.bookings.map((x) => (x.id === id ? saved : x)) });
    this.cacheNow();
  }

  /** Accepte ou refuse une demande en attente : passe par le serveur (peut créer l’événement Google). */
  async respondBooking(id: string, action: 'approve' | 'decline'): Promise<RespondResult> {
    const before = this.st.bookings;
    try {
      const r = await respondBookingApi(id, action);
      this.set({
        bookings: this.st.bookings.map((x) => (x.id === id ? { ...x, status: r.status, meetUrl: r.meetUrl ?? x.meetUrl } : x)),
      });
      this.cacheNow();
      return r;
    } catch (e) {
      this.set({ bookings: before });
      throw describeError(e);
    }
  }

  // ── Import / export ───────────────────────────────────────────────────────

  exportText(): string {
    return serializeExport(this.st.profile, this.active, this.now());
  }

  /** Ajoute les contacts d’une sauvegarde ; les doublons exacts sont ignorés ; le profil n’est repris que s’il n’en existe pas encore. */
  async importText(text: string): Promise<{ added: Contact[]; duplicates: number; skipped: number; demoSkipped: number; profileImported: boolean }> {
    const parsed = parseImport(text);
    const seen = new Set(this.active.map(contactSignature));
    const added: Contact[] = [];
    let duplicates = 0;
    for (const c of parsed.contacts) {
      const sig = contactSignature(c);
      if (seen.has(sig)) { duplicates++; continue; }
      seen.add(sig);
      const saved = await this.saveContact({ ...c, id: undefined, createdAt: undefined, updatedAt: undefined, deletedAt: null });
      added.push(saved);
    }
    let profileImported = false;
    if (parsed.profile && !this.st.profileSaved) {
      await this.saveProfile(parsed.profile);
      profileImported = true;
    }
    if (added.length) {
      const ids = new Set(added.map((c) => c.id));
      this.notify(
        `${added.length} contact${added.length > 1 ? 's' : ''} ajouté${added.length > 1 ? 's' : ''}` + (duplicates ? ` · ${duplicates} doublon${duplicates > 1 ? 's' : ''} ignoré${duplicates > 1 ? 's' : ''}` : '') + '.',
        'info',
        async () => { for (const id of ids) await this.purgeContact(id); },
      );
    }
    return { added, duplicates, skipped: parsed.skipped, demoSkipped: parsed.demoSkipped, profileImported };
  }
}
